import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ShopError, api, type OrderPromotion } from '../api';
import { formatCzk } from '../money';
import { recallOrder } from '../orderMemory';
import { usePageMeta } from '../seo';

interface OrderState {
  readonly total?: number;
  readonly email?: string;
  readonly paymentMethod?: string | null;
  /** Prezentacni mena + kurz z objednavky (settlement je dal CZK). */
  readonly currency?: string;
  readonly exchangeRate?: number;
  /** Akce z odpovedi checkoutu (server). Bez akci 0 a prazdne pole. */
  readonly promoDiscountMinor?: number;
  readonly promotions?: readonly OrderPromotion[];
}

/** Castka (halere CZK) v prezentacni mene ulozenym kurzem objednavky. */
function formatWithRate(minorCzk: number, currency: string, rate: number): string {
  const amount = (minorCzk / 100) * rate;
  const whole = Math.abs(amount % 1) < 0.005;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: whole ? 0 : 2,
    minimumFractionDigits: whole ? 0 : 2,
  }).format(amount);
}

/** Jak dlouho se ceka na potvrzeni z brany, nez to vzdame. */
const POLL_LIMIT = 10;
const POLL_DELAY_MS = 2_000;

export function ThankYouPage() {
  usePageMeta({ title: 'Děkujeme za objednávku | Super Krmivo', index: false });
  const { orderNumber = '' } = useParams();
  const state = (useLocation().state ?? {}) as OrderState;
  const [params] = useSearchParams();
  const gatewayResult = params.get('platba');

  // Po navratu z brany je stav z navigace pryc – e-mail si pamatuje
  // sessionStorage z okamziku, kdy zakaznik odesilal objednavku.
  const remembered = recallOrder(orderNumber);
  const email = state.email ?? remembered?.email ?? null;

  // Castka v prezentacni mene ulozenym kurzem, jinak v korunach – stejny vzor
  // jako u celkove castky nize.
  const showAmount = (minorCzk: number): string =>
    state.currency !== undefined && state.currency !== 'CZK' && state.exchangeRate !== undefined
      ? formatWithRate(minorCzk, state.currency, state.exchangeRate)
      : formatCzk(minorCzk);

  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(gatewayResult === 'ok');
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attempts = useRef(0);

  /**
   * Stav se cte ZE SERVERU, ne z parametru v adrese.
   *
   * Navrat z brany je obycejne presmerovani v prohlizeci – kdokoli si
   * muze dopsat `?platba=ok`. Zaplaceno je objednavka az tehdy, kdyz to
   * rekne nase API na zaklade notifikace od brany.
   */
  useEffect(() => {
    if (email === null || orderNumber === '') return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function check(): Promise<void> {
      try {
        const result = await api.paymentStatus(orderNumber, email as string);
        if (cancelled) return;
        setOrderStatus(result.orderStatus);
        setPaymentStatus(result.payment?.status ?? null);

        // Notifikace z brany muze dorazit o par vterin pozdeji nez
        // zakaznik. Chvili tedy pockame, nez reknem, ze platba nedosla.
        attempts.current += 1;
        const settled = result.orderStatus !== 'pending' || result.payment?.status === 'cancelled';
        if (settled || attempts.current >= POLL_LIMIT) {
          setWaiting(false);
          return;
        }
        if (gatewayResult === 'ok') timer = setTimeout(() => void check(), POLL_DELAY_MS);
      } catch {
        if (!cancelled) setWaiting(false);
      }
    }

    void check();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [email, orderNumber, gatewayResult]);

  const payAgain = useCallback(async () => {
    if (email === null || retrying) return;
    setRetrying(true);
    setError(null);
    try {
      const { redirectUrl } = await api.startPayment(orderNumber, email);
      if (redirectUrl === null) throw new ShopError(0, 'no_redirect', 'Bránu se nepodařilo otevřít.');
      window.location.href = redirectUrl;
    } catch (caught) {
      setError(caught instanceof ShopError ? caught.message : 'Platbu se nepodařilo zahájit.');
      setRetrying(false);
    }
  }, [email, orderNumber, retrying]);

  const paid = orderStatus !== null && orderStatus !== 'pending';
  const byTransfer = state.paymentMethod === 'bank_transfer';
  const byCard = gatewayResult !== null || state.paymentMethod === 'comgate';

  return (
    <div className="thanks">
      <span className="check" aria-hidden="true">✓</span>
      <h1>Děkujeme za objednávku</h1>
      <p className="order-number">
        Číslo objednávky <strong>{orderNumber}</strong>
      </p>

      {state.total !== undefined && (
        <p className="muted">
          {state.currency !== undefined &&
          state.currency !== 'CZK' &&
          state.exchangeRate !== undefined ? (
            <>
              Celkem {formatWithRate(state.total, state.currency, state.exchangeRate)}{' '}
              <span className="small">(účtováno v korunách {formatCzk(state.total)})</span>.
            </>
          ) : (
            <>Celkem k úhradě {formatCzk(state.total)}.</>
          )}
        </p>
      )}
      {email !== null && <p className="muted">Potvrzení pošleme na {email}.</p>}

      {/* Uplatnene akce ze serveru – dekovacka je jen vypise. */}
      {(state.promotions?.length ?? 0) > 0 && (
        <div className="notice promo-thanks">
          {(state.promoDiscountMinor ?? 0) > 0 && (
            <p className="promo-saved">Ušetřili jste {showAmount(state.promoDiscountMinor ?? 0)}</p>
          )}
          <ul className="promo-list">
            {state.promotions?.map((applied, index) => (
              <li key={`${applied.name}-${index}`}>
                <span>{applied.name}</span>
                <span className="promo-amount">− {showAmount(applied.discountMinor)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {byTransfer && (
        <div className="notice">
          <p>Zboží odešleme po připsání platby.</p>
          <p>
            Variabilní symbol je <strong>{orderNumber}</strong>, číslo účtu najdeš
            v potvrzovacím e-mailu.
          </p>
        </div>
      )}

      {byCard && waiting && (
        <p className="notice">
          <span className="spinner" aria-hidden="true" /> Ověřujeme platbu u banky…
        </p>
      )}

      {byCard && !waiting && paid && <p className="notice">Platba proběhla, děkujeme.</p>}

      {/* Faktura vzniká zaplacením – dřív odkaz nabízet nemá smysl. */}
      {paid && email !== null && (
        <p className="muted small">
          <a href={api.invoiceUrl(orderNumber, email)} target="_blank" rel="noreferrer">
            Stáhnout fakturu (PDF)
          </a>
        </p>
      )}

      {byCard && !waiting && !paid && (
        <div className="notice warn">
          <p>
            {gatewayResult === 'zruseno'
              ? 'Platba byla zrušena.'
              : paymentStatus === 'cancelled'
                ? 'Platba se nezdařila.'
                : 'Platbu jsme zatím nedostali.'}{' '}
            Objednávku máme uloženou – zaplatit můžeš i teď.
          </p>
          {email !== null && (
            <button type="button" className="button primary" onClick={() => void payAgain()} disabled={retrying}>
              {retrying ? 'Otevírám bránu…' : 'Zaplatit znovu'}
            </button>
          )}
          {error !== null && <p className="alert">{error}</p>}
        </div>
      )}

      <Link className="button" to="/">Zpátky do obchodu</Link>
    </div>
  );
}
